import React, { useState } from 'react';
import SEO from '../SEO/SEO';
import { getSEOData } from '../../data/seoData';
import { TOOL_TYPES, TOOL_CATEGORIES, getToolsByCategory } from '../../store/useToolStore';
import HorizontalToolScroller from './HorizontalToolScroller';

const TOOL_ROUTES = {
  [TOOL_TYPES.LOADER]: { path: '/tools/loader', icon: '⚙️', name: 'Loader Generator', description: 'Create animated loading indicators' },
  [TOOL_TYPES.GRADIENT]: { path: '/tools/gradient', icon: '🌈', name: 'Gradient Generator', description: 'Design beautiful gradients' },
  [TOOL_TYPES.CARD]: { path: '/tools/card', icon: '🪪', name: 'Card Generator', description: 'Build reusable card components' },
  [TOOL_TYPES.ANIMATION]: { path: '/tools/animation', icon: '⚡', name: 'Animation Generator', description: 'Create smooth animations' },
  [TOOL_TYPES.ALERT]: { path: '/tools/alert', icon: '🔔', name: 'Alert Generator', description: 'Custom alert dialogs' },
  [TOOL_TYPES.PHOTO_PICKER]: { path: '/tools/photo-picker', icon: '📸', name: 'Photo Picker', description: 'Camera & library integration' },
  [TOOL_TYPES.JSON_TO_MODEL]: { path: '/tools/json-to-model', icon: '🔍', name: 'JSON to Codable', description: 'Convert JSON to Swift models' },
  [TOOL_TYPES.API_GENERATOR]: { path: '/tools/api-generator', icon: '🌐', name: 'API Generator', description: 'Generate API client code' },
  [TOOL_TYPES.STORYBOARD_CONVERTER]: { path: '/tools/storyboard-converter', icon: '🧰', name: 'Storyboard Converter', description: 'UIKit to SwiftUI migration' },
  [TOOL_TYPES.POSTMAN_IMPORTER]: { path: '/tools/postman-importer', icon: '📝', name: 'Postman Importer', description: 'Import Postman collections' },
};

const Home = () => {
  const seoData = getSEOData('home');
  const [activeCategory, setActiveCategory] = useState(TOOL_CATEGORIES.SWIFTUI);
  
  // Filter out Storyboard Converter from the public SwiftUI tools list
  const swiftUITools = getToolsByCategory(TOOL_CATEGORIES.SWIFTUI)
    .filter((type) => type !== TOOL_TYPES.STORYBOARD_CONVERTER)
    .map(type => [type, TOOL_ROUTES[type]]);
  const swiftTools = getToolsByCategory(TOOL_CATEGORIES.SWIFT).map(type => [type, TOOL_ROUTES[type]]);

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        image={seoData.image}
        jsonLd={seoData.jsonLd}
        type="website"
      />
      <div className="bg-white dark:bg-gray-900 transition-colors duration-200">
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              SwiftToolsKit
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
              Generate Swift & SwiftUI code instantly. Free tools for iOS developers to create loaders, gradients, cards, animations, and more.
            </p>
          </div>
        </div>

        {/* Tools Showcase - Category Tabs + Horizontal Scroller */}
        <div id="tools" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-8">All Tools</h2>
          
          {/* Category Tabs */}
          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={() => setActiveCategory(TOOL_CATEGORIES.SWIFTUI)}
              className={`px-6 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeCategory === TOOL_CATEGORIES.SWIFTUI
                  ? 'bg-gradient-primary text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              SwiftUI (UI)
            </button>
            <button
              onClick={() => setActiveCategory(TOOL_CATEGORIES.SWIFT)}
              className={`px-6 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeCategory === TOOL_CATEGORIES.SWIFT
                  ? 'bg-gradient-primary text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              Swift (Functional)
            </button>
          </div>

          {/* Tools Scroller */}
          {activeCategory === TOOL_CATEGORIES.SWIFTUI ? (
            <HorizontalToolScroller tools={swiftUITools} />
          ) : (
            <HorizontalToolScroller tools={swiftTools} />
          )}
        </div>

        {/* Features Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-12">Why SwiftToolsKit?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 transition-colors duration-200">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Fast & Free</h3>
              <p className="text-gray-600 dark:text-gray-400">Generate production-ready Swift code in seconds. No signup required.</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 transition-colors duration-200">
              <div className="text-4xl mb-4">🎨</div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Visual Preview</h3>
              <p className="text-gray-600 dark:text-gray-400">See your code in action with real-time preview before copying.</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 transition-colors duration-200">
              <div className="text-4xl mb-4">📦</div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Copy & Paste</h3>
              <p className="text-gray-600 dark:text-gray-400">Get ready-to-use code that you can paste directly into Xcode.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;

